import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useArticles } from '@/queries/articles';
import { useBulkAddArticles, useArticleCollection } from '@/queries/article-collections';
import { CircularLoader } from './CircularLoader';
import CloseIcon from '@/assets/common/close.svg?react';
import CheckIcon from '@/assets/common/check.svg?react';
import styles from './AddToCollectionModal.module.scss';

interface AddToCollectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  collectionId: string;
  parentId?: string | null;
}

export function AddToCollectionModal({
  isOpen,
  onClose,
  collectionId,
  parentId = null,
}: AddToCollectionModalProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const { data: articles, isLoading: loadingArticles } = useArticles();
  const { data: collection } = useArticleCollection(collectionId);
  const bulkAddArticles = useBulkAddArticles();

  // Get IDs of articles already in collection
  const existingArticleIds = useMemo(() => {
    if (!collection?.items) return new Set<string>();
    return new Set(
      collection.items
        .filter((item) => item.item_type === 'article' && item.article_id)
        .map((item) => item.article_id!)
    );
  }, [collection]);

  // Filter articles
  const filteredArticles = useMemo(() => {
    if (!articles) return [];

    return articles.filter((article) => {
      // Exclude already added articles
      if (existingArticleIds.has(article.id)) return false;

      // Filter by search term
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        return (
          article.title.toLowerCase().includes(term) ||
          (article.excerpt && article.excerpt.toLowerCase().includes(term))
        );
      }

      return true;
    });
  }, [articles, existingArticleIds, searchTerm]);

  const handleToggle = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    if (selectedIds.size === 0) return;

    try {
      await bulkAddArticles.mutateAsync({
        collectionId,
        data: {
          article_ids: Array.from(selectedIds),
          parent_id: parentId,
        },
      });

      setSelectedIds(new Set());
      setSearchTerm('');
      onClose();
    } catch (err) {
      console.error('Failed to add articles:', err);
    }
  };

  const handleClose = () => {
    setSelectedIds(new Set());
    setSearchTerm('');
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className={styles.addToCollectionModal__overlay}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
        >
          <motion.div
            className={styles.addToCollectionModal__content}
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.addToCollectionModal__header}>
              <h2 className={styles.addToCollectionModal__title}>
                Add Articles to Collection
              </h2>
              <button
                className={styles.addToCollectionModal__closeButton}
                onClick={handleClose}
              >
                <CloseIcon />
              </button>
            </div>

            <div className={styles.addToCollectionModal__search}>
              <input
                type="text"
                className={styles.addToCollectionModal__searchInput}
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className={styles.addToCollectionModal__body}>
              {loadingArticles ? (
                <div className={styles.addToCollectionModal__loading}>
                  <CircularLoader />
                </div>
              ) : filteredArticles.length === 0 ? (
                <div className={styles.addToCollectionModal__empty}>
                  {searchTerm
                    ? 'No articles match your search'
                    : 'All your articles are already in this collection'}
                </div>
              ) : (
                <div className={styles.addToCollectionModal__list}>
                  {filteredArticles.map((article) => {
                    const isSelected = selectedIds.has(article.id);
                    return (
                      <div
                        key={article.id}
                        className={`${styles.addToCollectionModal__item} ${
                          isSelected ? styles['addToCollectionModal__item--selected'] : ''
                        }`}
                        onClick={() => handleToggle(article.id)}
                      >
                        <div
                          className={`${styles.addToCollectionModal__checkbox} ${
                            isSelected ? styles['addToCollectionModal__checkbox--checked'] : ''
                          }`}
                        >
                          {isSelected && <CheckIcon />}
                        </div>
                        <div className={styles.addToCollectionModal__itemContent}>
                          <h4 className={styles.addToCollectionModal__itemTitle}>
                            {article.title}
                          </h4>
                          <span className={styles.addToCollectionModal__itemMeta}>
                            {article.time_to_read_minutes} min read
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className={styles.addToCollectionModal__footer}>
              <span className={styles.addToCollectionModal__selected}>
                {selectedIds.size} selected
              </span>
              <div className={styles.addToCollectionModal__actions}>
                <button
                  className={`${styles.addToCollectionModal__button} ${styles['addToCollectionModal__button--secondary']}`}
                  onClick={handleClose}
                >
                  Cancel
                </button>
                <button
                  className={`${styles.addToCollectionModal__button} ${styles['addToCollectionModal__button--primary']}`}
                  onClick={handleSubmit}
                  disabled={selectedIds.size === 0 || bulkAddArticles.isPending}
                >
                  {bulkAddArticles.isPending ? 'Adding...' : 'Add Selected'}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
